import React, { useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import AuthClient from '../data/api/AuthClient';
import AuthHeroComponent from '../components/AuthHeroComponent';
import '../styles/AuthStyles.css';
import '../styles/LoginStyle.css';

interface ResetPasswordPageProps {
  title?: string;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ title }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { secret: pathSecret } = useParams<{ secret: string }>();
  const secret = searchParams.get('secret') || pathSecret || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const getStrength = (pwd: string): { label: string; color: string; width: string } => {
    if (pwd.length === 0) return { label: '', color: '#e2e8f0', width: '0%' };
    if (pwd.length < 6) return { label: 'Weak', color: '#ef4444', width: '25%' };
    if (pwd.length < 10 || !/[A-Z]/.test(pwd) || !/\d/.test(pwd)) return { label: 'Fair', color: '#f59e0b', width: '55%' };
    if (/[!@#$%^&*]/.test(pwd)) return { label: 'Strong', color: '#10b981', width: '100%' };
    return { label: 'Good', color: '#0d6e5b', width: '80%' };
  };

  const strength = getStrength(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (!secret) { setError('Invalid reset link. Please request a new one.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await AuthClient.resetPassword(password, secret);
      if (res.detail) {
        setSuccess(true);
      } else {
        setError(res.error || 'Failed to reset password. The link may have expired.');
      }
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-container">
      <AuthHeroComponent />
      <div className="login-form-container">
        <motion.div
          className="login-form-wrapper"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="brand-header" style={{ justifyContent: 'center' }}>
            <img src="/logo_login_name.png" alt="InterviewIQ Logo" className="brand-logo-img" />
          </div>

          {/* <div className="portal-label">CORPORATE PORTAL</div> */}

          {!success ? (
            <>
              <h2 className="welcome-title">{title ?? "Reset Password"}</h2>
              <p className="welcome-subtitle">Create a new secure password for your Joz HRM corporate account.</p>

              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group border-input-group">
                  <label className="field-label">New Password</label>
                  <div className="password-input-wrap">
                    <input
                      id="new-password"
                      className="form-input-line password-input"
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      autoFocus
                    />
                    <button type="button" className="password-toggle-btn" onClick={() => setShowPwd(!showPwd)} tabIndex={-1}>
                      {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {password.length > 0 && (
                  <motion.div className="pwd-strength-wrap" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ marginTop: -12, marginBottom: 16 }}>
                    <div className="pwd-strength-bar-bg">
                      <motion.div
                        className="pwd-strength-bar-fill"
                        style={{ backgroundColor: strength.color }}
                        initial={{ width: 0 }}
                        animate={{ width: strength.width }}
                        transition={{ duration: 0.4 }}
                      />
                    </div>
                    <span className="pwd-strength-label" style={{ color: strength.color }}>{strength.label}</span>
                  </motion.div>
                )}

                <div className="form-group border-input-group">
                  <label className="field-label">Confirm New Password</label>
                  <div className="password-input-wrap">
                    <input
                      id="confirm-password"
                      className="form-input-line password-input"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirm}
                      onChange={e => { setConfirm(e.target.value); setError(''); }}
                    />
                    <button type="button" className="password-toggle-btn" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}>
                      {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <motion.div className="auth-error-banner" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}>
                    <AlertCircle size={15} /> {error}
                  </motion.div>
                )}

                <button id="reset-password-btn" type="submit" className="btn-primary" disabled={loading}>
                  {loading ? <span className="auth-spinner" /> : <><ShieldCheck size={16} /> {title ?? "Reset Password"}</>}
                </button>
              </form>
            </>
          ) : (
            <div className="auth-success-state">
              <motion.div
                className="success-icon-ring"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              >
                <CheckCircle2 size={44} color="#0d6e5b" />
              </motion.div>
              <h2 className="welcome-title" style={{ textAlign: 'center' }}>
                {title === 'Create Password' ? 'Password Set!' : 'Password Reset!'}
              </h2>
              <p className="welcome-subtitle" style={{ textAlign: 'center' }}>
                {title === 'Create Password'
                  ? 'Your password has been set successfully. You can now sign in with your new credentials.'
                  : 'Your password has been updated successfully. You can now sign in with your new credentials.'}
              </p>
              <button className="btn-primary" onClick={() => navigate('/')} style={{ marginTop: 20 }}>
                Go to Login
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
